import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import OrderTable from "../../components/OrderManagement/OrderTable";
import { useFetchOrderDetails } from "../../hooks/useFetchOrderDetails";
import { useOrderModificationActions } from "hooks/useOrderModificationActions";
import ErrorSnackbar from "../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../widgets/Snackbar/SuccessSnackbar";
import { ORDER_MODIFICATION_STATUS_ID } from "constants/constants";

const ACTION_LABEL = {
  accept: "accepted",
  reject: "rejected",
};

const EMPTY_ORDERS = [];

const OrderModification = () => {
  const { studentId } = useParams();
  const {
    data,
    isLoading,
    error: fetchError,
    refetch,
  } = useFetchOrderDetails(studentId);

  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [dismissedFetchError, setDismissedFetchError] = useState(null);
  const [pendingActionMap, setPendingActionMap] = useState({});

  const fetchErrorMsg =
    fetchError && dismissedFetchError !== fetchError
      ? `Failed to load order details. ${fetchError.message}`
      : "";
  const displayedErrorMsg = errorMsg || fetchErrorMsg;

  const ordersData = data?.data?.order_details || EMPTY_ORDERS;

  const filteredOrders = ordersData.filter(
    (order) => order.status?.id === ORDER_MODIFICATION_STATUS_ID,
  );

  const getLatestActionableOrder = useCallback(
    async (order) => {
      const latestResult = await refetch();

      if (latestResult.error) {
        setErrorMsg(
          `Failed to verify latest order status. ${latestResult.error.message}`,
        );
        return null;
      }

      const latestOrders = latestResult.data?.data?.order_details || [];
      const latestOrder = latestOrders.find(
        (item) => item.order_id === order.order_id,
      );

      if (!latestOrder) {
        setErrorMsg("This order is no longer available. Please try again.");
        return null;
      }

      if (latestOrder.status?.id !== ORDER_MODIFICATION_STATUS_ID) {
        setErrorMsg(
          `This order is no longer actionable. Current status: ${
            latestOrder.status?.name || "-"
          }.`,
        );
        return null;
      }

      return latestOrder;
    },
    [refetch],
  );

  const handleSuccess = useCallback(
    (action) => {
      setSuccessMsg(`Order modification ${ACTION_LABEL[action]} successfully.`);
      refetch();
    },
    [refetch],
  );

  const handleError = useCallback((action, order, message) => {
    setErrorMsg(`Failed to ${action} modification request. ${message}`);
  }, []);

  const { acceptModification, rejectModification, isOrderLoading } =
    useOrderModificationActions({
      onSuccess: handleSuccess,
      onError: handleError,
    });

  const setPendingOrderAction = useCallback((orderId, action) => {
    setPendingActionMap((prev) => ({ ...prev, [orderId]: action }));
  }, []);

  const getOrderLoadingAction = useCallback(
    (orderId) => pendingActionMap[orderId] || isOrderLoading(orderId),
    [isOrderLoading, pendingActionMap],
  );

  const handleAccept = useCallback(
    async (order) => {
      setPendingOrderAction(order.order_id, "accept");

      try {
        const latestOrder = await getLatestActionableOrder(order);
        if (latestOrder) {
          await acceptModification(latestOrder);
        }
      } finally {
        setPendingOrderAction(order.order_id, null);
      }
    },
    [acceptModification, getLatestActionableOrder, setPendingOrderAction],
  );

  const handleReject = useCallback(
    async (order) => {
      setPendingOrderAction(order.order_id, "reject");

      try {
        const latestOrder = await getLatestActionableOrder(order);
        if (latestOrder) {
          await rejectModification(latestOrder);
        }
      } finally {
        setPendingOrderAction(order.order_id, null);
      }
    },
    [rejectModification, getLatestActionableOrder, setPendingOrderAction],
  );

  return (
    <>
      <OrderTable
        title="Order Modification"
        orders={filteredOrders}
        isLoading={isLoading}
        fetchError={fetchError}
        actionableStatusIds={[ORDER_MODIFICATION_STATUS_ID]}
        isOrderLoading={getOrderLoadingAction}
        onAccept={handleAccept}
        onReject={handleReject}
      />

      <ErrorSnackbar
        open={!!displayedErrorMsg}
        message={displayedErrorMsg}
        onClose={() => {
          if (errorMsg) {
            setErrorMsg("");
            return;
          }

          setDismissedFetchError(fetchError);
        }}
      />

      <SuccessSnackbar
        open={!!successMsg}
        message={successMsg}
        onClose={() => setSuccessMsg("")}
      />
    </>
  );
};

export default OrderModification;
